import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { useTheme } from '../context/ThemeContext';

const CompanyForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [company, setCompany] = useState(null);
  const [activeTab, setActiveTab] = useState('projects');
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [channels, setChannels] = useState([]);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [channelTypes, setChannelTypes] = useState([]);
  const [showChannelModal, setShowChannelModal] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [channelFormData, setChannelFormData] = useState({
    name: '',
    channel_type: '',
    credentials: {},
    settings: {},
    is_active: true
  });
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    website: '',
    industry: '',
    company_size: 'small'
  });

  const isEditing = Boolean(id);

  useEffect(() => {
    if (id) {
      fetchCompany();
      fetchProjects();
      fetchChannels();
      fetchChannelTypes();
    }
  }, [id]);

  // Handle tab query parameter
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['details', 'projects', 'channels'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  const fetchCompany = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/companies/${id}`);
      const companyData = response.data;
      setCompany(companyData);
      setFormData({
        name: companyData.name,
        slug: companyData.slug,
        description: companyData.description || '',
        website: companyData.website || '',
        industry: companyData.industry || '',
        company_size: companyData.company_size
      });
    } catch (error) {
      console.error('Error fetching company:', error);
      setError('Failed to load company');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      setLoadingProjects(true);
      const response = await api.get(`/companies/${id}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchChannels = async () => {
    try {
      setLoadingChannels(true);
      const response = await api.get(`/companies/${id}/channels`);
      setChannels(response.data.channels || []);
    } catch (error) {
      console.error('Error fetching channels:', error);
    } finally {
      setLoadingChannels(false);
    }
  };

  const fetchChannelTypes = async () => {
    try {
      const response = await api.get(`/companies/${id}/channels/types`);
      setChannelTypes(response.data.channel_types || []);
    } catch (error) {
      console.error('Error fetching channel types:', error);
    }
  };

  const openChannelModal = (channel = null) => {
    if (channel) {
      setEditingChannel(channel);
      setChannelFormData({
        name: channel.name,
        channel_type: channel.channel_type,
        credentials: channel.credentials || {},
        settings: channel.settings || {},
        is_active: channel.is_active
      });
    } else {
      setEditingChannel(null);
      setChannelFormData({
        name: '',
        channel_type: '',
        credentials: {},
        settings: {},
        is_active: true
      });
    }
    setShowChannelModal(true);
  };

  const closeChannelModal = () => {
    setShowChannelModal(false);
    setEditingChannel(null);
    setChannelFormData({
      name: '',
      channel_type: '',
      credentials: {},
      settings: {},
      is_active: true
    });
  };

  const handleChannelInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setChannelFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCredentialChange = (key, value) => {
    setChannelFormData(prev => ({
      ...prev,
      credentials: {
        ...prev.credentials,
        [key]: value
      }
    }));
  };

  const handleChannelSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (editingChannel) {
        await api.put(`/companies/${id}/channels/${editingChannel.id}`, channelFormData);
      } else {
        await api.post(`/companies/${id}/channels`, channelFormData);
      }
      closeChannelModal();
      fetchChannels();
    } catch (error) {
      console.error('Error saving channel:', error);
      setError(error.response?.data?.error || 'Failed to save channel');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteChannel = async (channelId) => {
    if (!window.confirm('Are you sure you want to delete this channel?')) {
      return;
    }

    try {
      setLoading(true);
      await api.delete(`/companies/${id}/channels/${channelId}`);
      fetchChannels();
    } catch (error) {
      console.error('Error deleting channel:', error);
      setError(error.response?.data?.error || 'Failed to delete channel');
    } finally {
      setLoading(false);
    }
  };

  const handleTestChannel = async (channelId) => {
    try {
      setLoading(true);
      const response = await api.post(`/companies/${id}/channels/${channelId}/test`);
      alert(response.data.message);
      fetchChannels(); // Refresh to update status
    } catch (error) {
      console.error('Error testing channel:', error);
      alert(error.response?.data?.message || 'Failed to test channel connection');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isEditing) {
        await api.put(`/companies/${id}`, formData);
      } else {
        await api.post('/companies', formData);
      }
      navigate('/companies');
    } catch (error) {
      console.error('Error saving company:', error);
      setError(error.response?.data?.error || error.response?.data?.message || `Failed to ${isEditing ? 'update' : 'create'} company`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // Auto-generate slug from name only when creating (not editing)
      ...(name === 'name' && !isEditing && { slug: value.toLowerCase().replace(/[^a-z0-9]+/g, '-') })
    }));
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to permanently delete this company? This action cannot be undone.')) {
      return;
    }
    
    try {
      setLoading(true);
      await api.delete(`/companies/${id}`);
      navigate('/companies');
    } catch (error) {
      console.error('Error deleting company:', error);
      setError(error.response?.data?.error || error.response?.data?.message || 'Failed to delete company');
      setLoading(false);
    }
  };

  const handleArchive = async () => {
    if (!window.confirm('Are you sure you want to archive this company? You can unarchive it later.')) {
      return;
    }
    
    try {
      setLoading(true);
      await api.post(`/companies/${id}/archive`);
      navigate('/companies');
    } catch (error) {
      console.error('Error archiving company:', error);
      setError(error.response?.data?.error || error.response?.data?.message || 'Failed to archive company');
      setLoading(false);
    }
  };

  const handleUnarchive = async () => {
    try {
      setLoading(true);
      const response = await api.post(`/companies/${id}/unarchive`);
      setCompany(response.data);
      setError('Company unarchived successfully!');
      // Refresh after a moment
      setTimeout(() => {
        setError(null);
        fetchCompany();
      }, 1500);
    } catch (error) {
      console.error('Error unarchiving company:', error);
      setError(error.response?.data?.error || error.response?.data?.message || 'Failed to unarchive company');
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditing) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb">
        <button
          onClick={() => navigate('/companies')}
          className="breadcrumb-link"
        >
          Companies
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">
          {isEditing ? 'Edit Company' : 'New Company'}
        </span>
      </nav>

      {/* Company Form Card */}
      <div className="card">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0'
            }}>
              {isEditing ? 'Edit Company' : 'Create New Company'}
            </h2>
            <p style={{
              color: 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              {isEditing ? 'Update company information' : 'Add a new company to your workspace'}
            </p>
          </div>
        </div>

        {error && (
          <div style={{
            backgroundColor: 'var(--red-50)',
            border: '1px solid var(--red-200)',
            color: 'var(--red-800)',
            padding: '0.75rem',
            borderRadius: '8px',
            marginBottom: '1rem'
          }}>
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ 
          display: 'flex', 
          gap: '1rem', 
          justifyContent: 'space-between',
          marginBottom: '1.5rem',
          marginTop: '5px',
          paddingBottom: '1.5rem',
          borderBottom: '1px solid var(--gray-200)'
        }}>
          <div style={{ display: 'flex', gap: '1rem' }}>
            {isEditing && company && (
              <>
                {company.is_active ? (
                  <button
                    type="button"
                    onClick={handleArchive}
                    disabled={loading}
                    style={{
                      backgroundColor: 'var(--yellow-50)',
                      color: 'var(--yellow-700)',
                      border: '1px solid var(--yellow-300)',
                      padding: '0.35rem 1rem',
                      borderRadius: '5px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      opacity: loading ? 0.6 : 1
                    }}
                  >
                    📦 Archive
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleUnarchive}
                    disabled={loading}
                    style={{
                      backgroundColor: 'var(--green-50)',
                      color: 'var(--green-700)',
                      border: '1px solid var(--green-300)',
                      padding: '0.35rem 1rem',
                      borderRadius: '5px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      opacity: loading ? 0.6 : 1
                    }}
                  >
                    📂 Unarchive
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={loading}
                  className="btn btn-danger"
                >
                  🗑️ Delete
                </button>
              </>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              type="button"
              onClick={() => navigate('/companies')}
              disabled={loading}
              className="btn btn-outline"
            >
              ✕ Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary"
              form="company-form"
              title="Update Company"
            >
              {loading ? 'Saving...' : (isEditing ? '💾 Update' : 'Create Company')}
            </button>
          </div>
        </div>

        <form id="company-form" onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600', 
                color: 'var(--gray-700)',
                fontSize: '0.875rem'
              }}>
                Company Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="Enter company name"
              />
            </div>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600', 
                color: 'var(--gray-700)',
                fontSize: '0.875rem'
              }}>
                Slug *
              </label>
              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleInputChange}
                required
                placeholder="company-slug"
                style={{ fontFamily: 'monospace' }}
              />
              <small style={{ color: 'var(--gray-500)', fontSize: '0.75rem' }}>
                Used in URLs and API calls
              </small>
            </div>
          </div>
          
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '0.5rem', 
              fontWeight: '600', 
              color: 'var(--gray-700)',
              fontSize: '0.875rem'
            }}>
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="4"
              placeholder="Brief description of the company"
            />
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem', marginBottom: '2rem' }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600', 
                color: 'var(--gray-700)',
                fontSize: '0.875rem'
              }}>
                Website
              </label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                placeholder="https://company.com"
              />
            </div>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600', 
                color: 'var(--gray-700)',
                fontSize: '0.875rem'
              }}>
                Industry
              </label>
              <input
                type="text"
                name="industry"
                value={formData.industry}
                onChange={handleInputChange}
                placeholder="e.g., Technology, Healthcare"
              />
            </div>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600', 
                color: 'var(--gray-700)',
                fontSize: '0.875rem'
              }}>
                Company Size
              </label>
              <select
                name="company_size"
                value={formData.company_size}
                onChange={handleInputChange}
              >
                <option value="startup">Startup (1-10 employees)</option>
                <option value="small">Small (11-50 employees)</option>
                <option value="medium">Medium (51-200 employees)</option>
                <option value="large">Large (201-1000 employees)</option>
                                <option value="enterprise">Enterprise (1000+ employees)</option>
              </select>
            </div>
          </div>
        </form>
      </div>

      {/* Tabs */}
      {isEditing && (
        <div className="card" style={{ marginTop: '1.5rem' }}>
          <div style={{
            display: 'flex',
            gap: '2rem',
            borderBottom: '2px solid var(--gray-200)',
            marginBottom: '1.5rem'
          }}>
            <button
              type="button"
              onClick={() => setActiveTab('projects')}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                padding: '1rem 0',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer',
                color: activeTab === 'projects' ? 'var(--primary-600)' : 'var(--gray-600)',
                borderBottom: activeTab === 'projects' ? '2px solid var(--primary-600)' : '2px solid transparent',
                marginBottom: '-2px',
                transition: 'all 0.2s'
              }}
            >
              📁 Projects ({projects.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('channels')}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                padding: '1rem 0',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer',
                color: activeTab === 'channels' ? 'var(--primary-600)' : 'var(--gray-600)',
                borderBottom: activeTab === 'channels' ? '2px solid var(--primary-600)' : '2px solid transparent',
                marginBottom: '-2px',
                transition: 'all 0.2s'
              }}
            >
              📡 Channels ({channels.length})
            </button>
          </div>

          {/* Projects Tab Content */}
          {activeTab === 'projects' && (
            <>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '1.5rem'
          }}>
            <h3 style={{ margin: 0, color: 'var(--gray-900)' }}>Projects</h3>
            <button
              onClick={() => navigate(`/companies/${id}/projects/new`)}
              className="btn btn-primary"
              title="Add Project"
            >
              ➕ Add
            </button>
          </div>

          {loadingProjects ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <div className="spinner"></div>
            </div>
          ) : projects.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '3rem',
              color: isDark ? '#9ca3af' : 'var(--gray-500)'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📁</div>
              <p style={{ margin: '0 0 1rem 0' }}>No projects yet</p>
              <button
                onClick={() => navigate(`/companies/${id}/projects/new`)}
                className="btn btn-primary"
              >
                Create First Project
              </button>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: isDark ? '#374151' : 'var(--gray-50)', borderBottom: `1px solid ${isDark ? '#4b5563' : 'var(--gray-200)'}` }}>
                  <th style={{ 
                    padding: '1rem', 
                    textAlign: 'left', 
                    fontWeight: '600', 
                    color: isDark ? '#ffffff' : 'var(--gray-700)',
                    fontSize: '0.875rem'
                  }}>
                    Project Name
                  </th>
                  <th style={{ 
                    padding: '1rem', 
                    textAlign: 'left', 
                    fontWeight: '600', 
                    color: isDark ? '#ffffff' : 'var(--gray-700)',
                    fontSize: '0.875rem'
                  }}>
                    Type
                  </th>
                  <th style={{ 
                    padding: '1rem', 
                    textAlign: 'left', 
                    fontWeight: '600', 
                    color: isDark ? '#ffffff' : 'var(--gray-700)',
                    fontSize: '0.875rem'
                  }}>
                    Status
                  </th>
                  <th style={{ 
                    padding: '1rem', 
                    textAlign: 'left', 
                    fontWeight: '600', 
                    color: isDark ? '#ffffff' : 'var(--gray-700)',
                    fontSize: '0.875rem'
                  }}>
                    Budget
                  </th>
                  <th style={{ 
                    padding: '1rem', 
                    textAlign: 'center', 
                    fontWeight: '600', 
                    color: isDark ? '#ffffff' : 'var(--gray-700)',
                    fontSize: '0.875rem'
                  }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {projects.map((project, index) => (
                  <tr 
                    key={project.id}
                    style={{ 
                      backgroundColor: isDark ? '#1f2937' : 'transparent',
                      borderBottom: index < projects.length - 1 ? `1px solid ${isDark ? '#374151' : 'var(--gray-100)'}` : 'none'
                    }}
                  >
                    <td style={{ padding: '1rem' }}>
                      <div style={{ fontWeight: '600', color: isDark ? '#ffffff' : 'var(--gray-900)' }}>
                        {project.name}
                      </div>
                      {project.description && (
                        <div style={{ fontSize: '0.8rem', color: isDark ? '#9ca3af' : 'var(--gray-600)', marginTop: '0.25rem' }}>
                          {project.description}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '1rem', color: isDark ? '#e5e7eb' : 'var(--gray-700)', fontSize: '0.875rem' }}>
                      {project.project_type}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <span className={`badge badge-${
                        project.status === 'completed' ? 'success' :
                        project.status === 'active' ? 'primary' :
                        project.status === 'planning' ? 'info' : 'secondary'
                      }`}>
                        {project.status}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', color: isDark ? '#e5e7eb' : 'var(--gray-700)', fontSize: '0.875rem' }}>
                      {project.budget ? `$${project.budget.toLocaleString()}` : '-'}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'right' }}>
                      <button
                        onClick={() => navigate(`/companies/${id}/projects/${project.id}/edit`)}
                        style={{
                          backgroundColor: isDark ? '#374151' : 'var(--gray-100)',
                          color: isDark ? '#e5e7eb' : 'var(--gray-700)',
                          border: `1px solid ${isDark ? '#4b5563' : 'var(--gray-300)'}`,
                          padding: '0.5rem 0.75rem',
                          borderRadius: '8px',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          cursor: 'pointer'
                        }}
                      >
                        ✏️ Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
            </>
          )}

          {/* Channels Tab Content */}
          {activeTab === 'channels' && (
            <>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '1.5rem'
              }}>
                <h3 style={{ margin: 0, color: 'var(--gray-900)' }}>Marketing Channels</h3>
                <button
                  onClick={() => openChannelModal()}
                  className="btn btn-primary"
                  title="Add Channel"
                >
                  ➕ Add Channel
                </button>
              </div>

              {loadingChannels ? (
                <div style={{ textAlign: 'center', padding: '3rem' }}>
                  <div className="spinner"></div>
                </div>
              ) : channels.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '3rem',
                  color: 'var(--gray-500)'
                }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📡</div>
                  <p style={{ margin: '0 0 1rem 0' }}>No channels configured yet</p>
                  <p style={{ margin: '0 0 1.5rem 0', fontSize: '0.875rem' }}>
                    Connect your marketing channels to track campaigns and analytics
                  </p>
                  <button
                    onClick={() => openChannelModal()}
                    className="btn btn-primary"
                  >
                    Add First Channel
                  </button>
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                  gap: '1.5rem'
                }}>
                  {channels.map(channel => {
                    const channelConfig = channelTypes.find(ct => ct.value === channel.channel_type)?.config || {};
                    const statusColors = {
                      active: { bg: 'var(--green-50)', border: 'var(--green-200)', text: 'var(--green-700)' },
                      pending: { bg: 'var(--yellow-50)', border: 'var(--yellow-200)', text: 'var(--yellow-700)' },
                      error: { bg: 'var(--red-50)', border: 'var(--red-200)', text: 'var(--red-700)' },
                      inactive: { bg: 'var(--gray-50)', border: 'var(--gray-200)', text: 'var(--gray-700)' }
                    };
                    const statusColor = statusColors[channel.status] || statusColors.inactive;

                    return (
                      <div
                        key={channel.id}
                        style={{
                          background: 'white',
                          border: '1px solid var(--gray-200)',
                          borderRadius: '8px',
                          padding: '1.5rem',
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          alignItems: 'start',
                          justifyContent: 'space-between',
                          marginBottom: '1rem'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
                            <div style={{
                              fontSize: '2rem',
                              lineHeight: 1
                            }}>
                              {channelConfig.icon || '🔌'}
                            </div>
                            <div style={{ flex: 1 }}>
                              <h4 style={{
                                margin: '0 0 0.25rem 0',
                                color: 'var(--gray-900)',
                                fontSize: '1rem',
                                fontWeight: '600'
                              }}>
                                {channel.name}
                              </h4>
                              <div style={{
                                fontSize: '0.75rem',
                                color: 'var(--gray-600)'
                              }}>
                                {channelConfig.name || channel.channel_type}
                              </div>
                            </div>
                          </div>
                          <div style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            backgroundColor: statusColor.bg,
                            border: `1px solid ${statusColor.border}`,
                            color: statusColor.text,
                            textTransform: 'uppercase'
                          }}>
                            {channel.status}
                          </div>
                        </div>

                        {channel.last_error && (
                          <div style={{
                            fontSize: '0.75rem',
                            color: 'var(--red-700)',
                            backgroundColor: 'var(--red-50)',
                            border: '1px solid var(--red-200)',
                            padding: '0.5rem',
                            borderRadius: '4px',
                            marginBottom: '1rem'
                          }}>
                            ⚠️ {channel.last_error}
                          </div>
                        )}

                        {channel.last_sync && (
                          <div style={{
                            fontSize: '0.75rem',
                            color: 'var(--gray-600)',
                            marginBottom: '1rem'
                          }}>
                            Last synced: {new Date(channel.last_sync).toLocaleString()}
                          </div>
                        )}

                        <div style={{
                          display: 'flex',
                          gap: '0.5rem',
                          paddingTop: '1rem',
                          borderTop: '1px solid var(--gray-200)'
                        }}>
                          <button
                            onClick={() => handleTestChannel(channel.id)}
                            disabled={loading}
                            style={{
                              flex: 1,
                              backgroundColor: 'var(--blue-50)',
                              color: 'var(--blue-700)',
                              border: '1px solid var(--blue-200)',
                              padding: '0.5rem',
                              borderRadius: '6px',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              cursor: loading ? 'not-allowed' : 'pointer',
                              opacity: loading ? 0.6 : 1
                            }}
                          >
                            🔄 Test
                          </button>
                          <button
                            onClick={() => openChannelModal(channel)}
                            disabled={loading}
                            style={{
                              flex: 1,
                              backgroundColor: 'var(--gray-50)',
                              color: 'var(--gray-700)',
                              border: '1px solid var(--gray-300)',
                              padding: '0.5rem',
                              borderRadius: '6px',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              cursor: loading ? 'not-allowed' : 'pointer',
                              opacity: loading ? 0.6 : 1
                            }}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={() => handleDeleteChannel(channel.id)}
                            disabled={loading}
                            style={{
                              backgroundColor: 'var(--red-50)',
                              color: 'var(--red-700)',
                              border: '1px solid var(--red-200)',
                              padding: '0.5rem 0.75rem',
                              borderRadius: '6px',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              cursor: loading ? 'not-allowed' : 'pointer',
                              opacity: loading ? 0.6 : 1
                            }}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Channel Modal */}
      {showChannelModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '2rem'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }}>
            <div style={{
              padding: '1.5rem',
              borderBottom: '1px solid var(--gray-200)',
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
              zIndex: 1
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <h3 style={{
                  margin: 0,
                  color: 'var(--gray-900)',
                  fontSize: '1.25rem',
                  fontWeight: '600'
                }}>
                  {editingChannel ? 'Edit Channel' : 'Add New Channel'}
                </h3>
                <button
                  onClick={closeChannelModal}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: 'var(--gray-500)',
                    padding: '0',
                    lineHeight: 1
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            <form onSubmit={handleChannelSubmit} style={{ padding: '1.5rem' }}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '600', 
                  color: 'var(--gray-700)',
                  fontSize: '0.875rem'
                }}>
                  Channel Type *
                </label>
                <select
                  name="channel_type"
                  value={channelFormData.channel_type}
                  onChange={handleChannelInputChange}
                  required
                  disabled={editingChannel !== null}
                >
                  <option value="">Select channel type...</option>
                  {channelTypes.map(ct => (
                    <option key={ct.value} value={ct.value}>
                      {ct.config.icon || '🔌'} {ct.config.name || ct.value}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '600', 
                  color: 'var(--gray-700)',
                  fontSize: '0.875rem'
                }}>
                  Channel Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={channelFormData.name}
                  onChange={handleChannelInputChange}
                  placeholder="e.g., Primary Facebook Account"
                  required
                />
              </div>

              {channelFormData.channel_type && channelTypes.find(ct => ct.value === channelFormData.channel_type)?.config?.credentials && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h4 style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: 'var(--gray-900)',
                    marginBottom: '1rem'
                  }}>
                    Authentication
                  </h4>
                  {channelTypes
                    .find(ct => ct.value === channelFormData.channel_type)
                    ?.config?.credentials.map(cred => (
                      <div key={cred.key} style={{ marginBottom: '1rem' }}>
                        <label style={{ 
                          display: 'block', 
                          marginBottom: '0.5rem', 
                          fontWeight: '600', 
                          color: 'var(--gray-700)',
                          fontSize: '0.875rem'
                        }}>
                          {cred.label} {cred.required && '*'}
                        </label>
                        <input
                          type={cred.type}
                          value={channelFormData.credentials[cred.key] || ''}
                          onChange={(e) => handleCredentialChange(cred.key, e.target.value)}
                          required={cred.required}
                          placeholder={`Enter ${cred.label.toLowerCase()}`}
                        />
                      </div>
                    ))}
                </div>
              )}

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={channelFormData.is_active}
                    onChange={handleChannelInputChange}
                  />
                  <span style={{
                    fontWeight: '600',
                    color: 'var(--gray-700)',
                    fontSize: '0.875rem'
                  }}>
                    Channel is active
                  </span>
                </label>
              </div>

              <div style={{
                display: 'flex',
                gap: '1rem',
                paddingTop: '1rem',
                borderTop: '1px solid var(--gray-200)'
              }}>
                <button
                  type="button"
                  onClick={closeChannelModal}
                  disabled={loading}
                  className="btn btn-outline"
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn btn-primary"
                  style={{ flex: 1 }}
                >
                  {loading ? 'Saving...' : (editingChannel ? 'Update Channel' : 'Add Channel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyForm;
